'use client';

import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useKnowledgeTrainingStatus } from '@/hooks/use-knowledge-training-status';

const KNOWLEDGE_TRAINING_TOAST = 'knowledge-training-toast';
const KNOWLEDGE_TRAINING_TOAST_DISMISS = 'knowledge-training-toast-dismiss';

export function dismissTrainingToast(sourceId: string) {
  toast.dismiss(sourceId);
  window.dispatchEvent(
    new CustomEvent(KNOWLEDGE_TRAINING_TOAST_DISMISS, { detail: { sourceId } })
  );
}

export function showTrainingToast(sourceId: string, sourceName?: string) {
  dismissTrainingToast(sourceId);
  const label = sourceName ? `Training ${sourceName}...` : 'Training knowledge source...';
  toast.loading(label, { id: sourceId, duration: Infinity });
  window.dispatchEvent(
    new CustomEvent(KNOWLEDGE_TRAINING_TOAST, {
      detail: { sourceId, sourceName },
    })
  );
}

function KnowledgeTrainingToastPoller({
  sourceId,
  sourceName,
  onComplete,
}: {
  sourceId: string;
  sourceName?: string;
  onComplete: () => void;
}) {
  const { status, isComplete, isError, retry } = useKnowledgeTrainingStatus({
    sourceId,
    enabled: true,
    pollIntervalMs: 3000,
  });

  useEffect(() => {
    if (!status) return;

    if (isComplete) {
      toast.success('Training complete', { id: sourceId, duration: 5000 });
      onComplete();
      return;
    }

    if (isError) {
      toast.error(status.errorMessage || 'Training failed. Please try again.', {
        id: sourceId,
        duration: Infinity,
        action: { label: 'Retry', onClick: () => retry() },
      });
      onComplete();
      return;
    }

    const label = sourceName ? `Training ${sourceName}...` : 'Training knowledge source...';
    toast.loading(label, { id: sourceId, duration: Infinity });
  }, [sourceId, sourceName, status, isComplete, isError, onComplete, retry]);

  return null;
}

export function KnowledgeTrainingToasts() {
  const [sources, setSources] = useState<{ sourceId: string; sourceName?: string }[]>([]);

  useEffect(() => {
    const handleAdd = (e: CustomEvent<{ sourceId: string; sourceName?: string }>) => {
      const { sourceId, sourceName } = e.detail;
      setSources((prev) =>
        prev.some((s) => s.sourceId === sourceId) ? prev : [...prev, { sourceId, sourceName }]
      );
    };

    const handleDismiss = (e: CustomEvent<{ sourceId: string }>) => {
      const { sourceId } = e.detail;
      setSources((prev) => prev.filter((s) => s.sourceId !== sourceId));
    };

    window.addEventListener(KNOWLEDGE_TRAINING_TOAST, handleAdd as EventListener);
    window.addEventListener(KNOWLEDGE_TRAINING_TOAST_DISMISS, handleDismiss as EventListener);

    return () => {
      window.removeEventListener(KNOWLEDGE_TRAINING_TOAST, handleAdd as EventListener);
      window.removeEventListener(KNOWLEDGE_TRAINING_TOAST_DISMISS, handleDismiss as EventListener);
    };
  }, []);

  return (
    <>
      {sources.map(({ sourceId, sourceName }) => (
        <KnowledgeTrainingToastPoller
          key={sourceId}
          sourceId={sourceId}
          sourceName={sourceName}
          onComplete={() => setSources((prev) => prev.filter((s) => s.sourceId !== sourceId))}
        />
      ))}
    </>
  );
}
